import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton, Text } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { InvestmentDetailsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/InvestmentDetailsCard.tsx';
import { CardTableViewSelector } from '@dealstack/frontend/shared/components/CardTableViewSelector/CardTableViewSelector.tsx';
import { usePersistedCardTableView } from '@dealstack/frontend/shared/components/CardTableViewSelector/usePersistedCardTableView.ts';
import { StorageKeys } from '@dealstack/frontend/shared/helper/storageUtils.ts';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable.tsx';
import { useMantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations.ts';
import { InvestorDetailsEmptyCardState } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsEmptyCardState/InvestorDetailsEmptyCardState.tsx';
import { EMPTY_CARD_STATE_HEIGHT } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsEmptyCardState/InvestorDetailsEmptyCardState.styles.tsx';
import { RelatedPeopleCards } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestmentVehicleDetails/components/RelatedPeopleCard/RelatedPeopleCards.tsx';
import { getRelatedPeopleTableColumns } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestmentVehicleDetails/components/RelatedPeopleCard/relatedPeopleTable.utils.tsx';
import { useVehicleRelatedPeople } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestmentVehicleDetails/components/RelatedPeopleCard/useVehicleRelatedPeople.ts';
import { investorDetailsPath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';

interface Props {
	dealId: string;
	mipId: string;
	personId: string;
}

export const RelatedPeopleCard = ({ dealId, mipId, personId }: Props) => {
	const tMipManage = useTranslationMipManage();
	const dataOperations = useMantineTableDataOperations();
	const { cards, isLoading, isError } = useVehicleRelatedPeople({ mipId, personId });
	const navigate = useNavigate();

	const getInvestorPath = useCallback(
		(investorId: string) => investorDetailsPath({ dealId, mipId, investorId }),
		[dealId, mipId]
	);

	const [view, setView] = usePersistedCardTableView(
		StorageKeys.MIP_INVESTMENT_VEHICLE_DETAILS_RELATED_PEOPLE_VIEW,
		'cards'
	);

	const columns = useMemo(() => getRelatedPeopleTableColumns({ tMipManage }), [tMipManage]);

	const renderContent = () => {
		if (isLoading) {
			return <Skeleton height={EMPTY_CARD_STATE_HEIGHT} radius="0.5rem" />;
		}

		if (isError) {
			return (
				<InvestorDetailsEmptyCardState
					icon="es-person-boxes"
					title={tMipManage('investors.details.related_people.error_title')}
				/>
			);
		}

		if (!cards.length) {
			return (
				<InvestorDetailsEmptyCardState
					icon="es-person-boxes"
					title={tMipManage('investors.details.related_people.empty_title')}
					description={tMipManage('investors.details.related_people.empty_description')}
				/>
			);
		}

		if (view === 'cards') {
			return <RelatedPeopleCards cards={cards} getInvestorPath={getInvestorPath} />;
		}

		return (
			<MantineTable
				columns={columns}
				data={cards}
				dataOperations={dataOperations}
				manualFiltering={false}
				manualSorting={false}
				enableTopToolbar={false}
				enablePagination={false}
				mantineTableBodyRowProps={({ row }) => {
					const { investorId } = row.original;

					return investorId
						? {
								onClick: () => navigate(getInvestorPath(investorId)),
								style: { cursor: 'pointer' },
							}
						: {};
				}}
			/>
		);
	};

	return (
		<div
			data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTMENT_VEHICLE_DETAILS_RELATED_PEOPLE_CARD}
		>
			<InvestmentDetailsCard
				cardLabelComponent={
					<Text fw={600} c="dark.8" className="label">
						{tMipManage('investors.details.related_people.title')}
					</Text>
				}
				actions={<CardTableViewSelector value={view} onViewChange={setView} />}
				variant="borderless"
			>
				{renderContent()}
			</InvestmentDetailsCard>
		</div>
	);
};
